import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ContactOrAccountDetailsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {AccountAttributeUpdateAction} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AccountCustomAttributesContainer} from './accountCustomAttributesContainer';
import {DescriptionContainer} from './descriptionContainer';
import {EmailDomains} from './emailDomains';

/*
 * Props.
 */

interface AccountInformationProps {
  account: AccountModel;
  panelId: ContactOrAccountDetailsPanelId;
  shouldDisableActions?: boolean;
  updateAccountAttributeAsync: (action: AccountAttributeUpdateAction) => Promise<void>;
  updateUnsavedAccount: (action: AccountAttributeUpdateAction) => void;
}

/*
 * Style.
 */

const StyledEmptyStateDiv = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const AccountInformation: FC<AccountInformationProps> = (props) => {
  const {account, panelId, shouldDisableActions, updateAccountAttributeAsync, updateUnsavedAccount} = props;
  const shouldShowEmptyState = computeShouldShowEmptyState(account);
  if (shouldShowEmptyState) {
    return (
      <StyledEmptyStateDiv>
        <Trans id="account-information-no-information-empty-state">No information</Trans>
      </StyledEmptyStateDiv>
    );
  }

  return (
    <>
      <DescriptionContainer
        account={account}
        shouldDisableActions={shouldDisableActions}
        updateAccountAttributeAsync={updateAccountAttributeAsync}
        updateUnsavedAccount={updateUnsavedAccount}
      />
      <EmailDomains account={account} panelId={panelId} shouldDisableActions={shouldDisableActions} />
      <AccountCustomAttributesContainer
        account={account}
        shouldDisableActions={shouldDisableActions}
        updateAccountAttributeAsync={updateAccountAttributeAsync}
        updateUnsavedAccount={updateUnsavedAccount}
      />
    </>
  );
};

/*
 * Helpers.
 */

function computeShouldShowEmptyState(account: AccountModel) {
  if (
    account.description !== undefined ||
    account.domains.length > 0 ||
    (account.customFieldAttributes && account.customFieldAttributes.length > 0)
  ) {
    return false;
  }

  return true;
}
